import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  settingsApi,
  TenantSettings,
  AiPersonasConfig,
  AttendanceMode,
  requestSlice,
  type RequestSettingsSlice,
} from "@/lib/settings-api";
import { ApiError } from "@/lib/api";
import { sitesApi } from "@/lib/sites-api";
import { useLocale, useT } from "@/i18n/LocaleContext";
import { SUPPORTED_LOCALES, Locale } from "@/i18n/dict";
import RequestsSettingsTab from "./settings/RequestsSettingsTab";

const TIMEZONES = [
  { value: "Asia/Kuala_Lumpur", label: "Asia/Kuala_Lumpur (UTC+8)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (UTC+8)" },
  { value: "Asia/Jakarta", label: "Asia/Jakarta (UTC+7)" },
  { value: "Asia/Bangkok", label: "Asia/Bangkok (UTC+7)" },
];

const DATE_FORMATS = ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"] as const;

interface FormState {
  organizationName: string;
  defaultLanguage: Locale;
  timezone: string;
  dateFormat: string;
  gracePeriodMins: number;
  lateThresholdMins: number;
  minWorkHoursPerDay: number;
  requireApprovalForCorrections: boolean;
  autoFlagExceptions: boolean;
  defaultWorkStartTime: string;
  workingDays: number[];
  defaultWorkEndTime: string;
  attendanceMode: AttendanceMode;
  geofenceEnforcementEnabled: boolean;
  allowGpsOnlyFallback: boolean;
  defaultGeofenceRadiusM: number;
  wfhEnabled: boolean;
  defaultHomeRadiusM: number;
  webCheckInEnabled: boolean;
  maxCheckInAccuracyM: number;
  wfaEnabled: boolean;
  emailDigestEnabled: boolean;
  inAppNotificationsEnabled: boolean;
  mobilePushEnabled: boolean;
  aiEnableHR: boolean;
  aiEnableSup: boolean;
  aiEnableMobile: boolean;
  aiResponseStyle: "concise" | "detailed";
}

function toForm(s: TenantSettings): FormState {
  const ai = (s.aiPersonasConfig ?? {}) as AiPersonasConfig;
  const lang = (s.defaultLanguage === "ms" ? "ms" : "en") as Locale;
  return {
    organizationName: s.organizationName ?? "",
    defaultLanguage: lang,
    timezone: s.timezone,
    dateFormat: s.dateFormat,
    gracePeriodMins: s.gracePeriodMins,
    lateThresholdMins: s.lateThresholdMins,
    minWorkHoursPerDay: s.minWorkHoursPerDay,
    requireApprovalForCorrections: s.requireApprovalForCorrections,
    autoFlagExceptions: s.autoFlagExceptions,
    defaultWorkStartTime: s.defaultWorkStartTime,
    workingDays: s.workingDays ?? [1, 2, 3, 4, 5],
    defaultWorkEndTime: s.defaultWorkEndTime,
    attendanceMode: s.attendanceMode,
    geofenceEnforcementEnabled: s.geofenceEnforcementEnabled,
    allowGpsOnlyFallback: s.allowGpsOnlyFallback,
    defaultGeofenceRadiusM: s.defaultGeofenceRadiusM,
    wfhEnabled: s.wfhEnabled,
    defaultHomeRadiusM: s.defaultHomeRadiusM,
    webCheckInEnabled: s.webCheckInEnabled,
    maxCheckInAccuracyM: s.maxCheckInAccuracyM,
    wfaEnabled: s.wfaEnabled,
    emailDigestEnabled: s.emailDigestEnabled,
    inAppNotificationsEnabled: s.inAppNotificationsEnabled,
    mobilePushEnabled: s.mobilePushEnabled,
    aiEnableHR: ai.enableForHR ?? true,
    aiEnableSup: ai.enableForSupervisors ?? true,
    aiEnableMobile: ai.enableForMobile ?? false,
    aiResponseStyle: ai.defaultResponseStyle ?? "concise",
  };
}

const Settings = () => {
  const t = useT();
  const { setLocale } = useLocale();
  const [loading, setLoading] = useState(true);
  const [inheritingSites, setInheritingSites] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState<FormState | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  // Saved separately through PATCH /settings/requests (FEAT-2026-027), so it
  // stays out of FormState and the page's own Save.
  const [requestSettings, setRequestSettings] = useState<RequestSettingsSlice | null>(null);
  // The Requests tab has its own Cancel / Save changes; the page's footer
  // saves the other tabs, so it isn't shown there (BUG-2026-023).
  const [tab, setTab] = useState("general");

  useEffect(() => {
    let cancelled = false;
    // How many work locations take their radius from this default. Editing the
    // number resizes their geofences, and a geofence decides who can physically
    // clock in — so the blast radius is shown before the change, not after.
    sitesApi
      .list({ pageSize: 200 })
      .then((r) => {
        if (!cancelled) {
          setInheritingSites(r.data.filter((x) => x.geofenceRadiusM == null).length);
        }
      })
      .catch(() => { /* non-fatal: the field still works without the hint */ });
    settingsApi
      .get()
      .then((data) => {
        if (cancelled) return;
        const f = toForm(data);
        setInitial(f);
        setForm(f);
        setRequestSettings(requestSlice(data));
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e instanceof ApiError ? e.message : "Could not load settings");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = useMemo(() => {
    if (!form || !initial) return false;
    return (Object.keys(form) as (keyof FormState)[]).some((k) => form[k] !== initial[k]);
  }, [form, initial]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };

  const handleLanguage = (value: Locale) => {
    set("defaultLanguage", value);
    setLocale(value);
  };

  const handleReset = () => {
    if (!initial) return;
    setForm(initial);
    setLocale(initial.defaultLanguage);
  };

  const handleSave = async () => {
    if (!form || !dirty) return;
    setSaving(true);
    try {
      const aiPersonasConfig: AiPersonasConfig = {
        enableForHR: form.aiEnableHR,
        enableForSupervisors: form.aiEnableSup,
        enableForMobile: form.aiEnableMobile,
        defaultResponseStyle: form.aiResponseStyle,
      };
      const updated = await settingsApi.update({
        organizationName: form.organizationName,
        defaultLanguage: form.defaultLanguage,
        timezone: form.timezone,
        dateFormat: form.dateFormat,
        gracePeriodMins: form.gracePeriodMins,
        lateThresholdMins: form.lateThresholdMins,
        minWorkHoursPerDay: form.minWorkHoursPerDay,
        requireApprovalForCorrections: form.requireApprovalForCorrections,
        autoFlagExceptions: form.autoFlagExceptions,
        defaultWorkStartTime: form.defaultWorkStartTime,
        workingDays: form.workingDays,
        defaultWorkEndTime: form.defaultWorkEndTime,
        attendanceMode: form.attendanceMode,
        geofenceEnforcementEnabled: form.geofenceEnforcementEnabled,
        allowGpsOnlyFallback: form.allowGpsOnlyFallback,
        defaultGeofenceRadiusM: form.defaultGeofenceRadiusM,
        wfhEnabled: form.wfhEnabled,
        defaultHomeRadiusM: form.defaultHomeRadiusM,
        webCheckInEnabled: form.webCheckInEnabled,
        maxCheckInAccuracyM: form.maxCheckInAccuracyM,
        wfaEnabled: form.wfaEnabled,
        emailDigestEnabled: form.emailDigestEnabled,
        inAppNotificationsEnabled: form.inAppNotificationsEnabled,
        mobilePushEnabled: form.mobilePushEnabled,
        aiPersonasConfig,
      });
      const next = toForm(updated);
      setInitial(next);
      setForm(next);
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t("settings.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-24">
      <Tabs value={tab} onValueChange={setTab} className="space-y-5">
        <TabsList className="bg-card border flex-wrap h-auto">
          <TabsTrigger value="general">{t("settings.tabs.general")}</TabsTrigger>
          <TabsTrigger value="rules">{t("settings.tabs.rules")}</TabsTrigger>
          <TabsTrigger value="requests">{t("settings.tabs.requests")}</TabsTrigger>
          <TabsTrigger value="hours">{t("settings.tabs.hours")}</TabsTrigger>
          <TabsTrigger value="geo">{t("settings.tabs.geo")}</TabsTrigger>
          <TabsTrigger value="notif">{t("settings.tabs.notif")}</TabsTrigger>
          <TabsTrigger value="data">{t("settings.tabs.data")}</TabsTrigger>
          <TabsTrigger value="ai">{t("settings.tabs.ai")}</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <SettingsCard title={t("settings.general.title")} desc={t("settings.general.desc")}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("settings.general.orgName")}>
                <Input
                  value={form.organizationName}
                  onChange={(e) => set("organizationName", e.target.value)}
                />
              </Field>
              <Field label={t("settings.general.language")}>
                <Select value={form.defaultLanguage} onValueChange={(v) => handleLanguage(v as Locale)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LOCALES.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("settings.general.timezone")}>
                <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("settings.general.dateFormat")}>
                <Select value={form.dateFormat} onValueChange={(v) => set("dateFormat", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DATE_FORMATS.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </SettingsCard>
        </TabsContent>

        <TabsContent value="rules" className="space-y-4">
          <SettingsCard title={t("settings.rules.title")} desc={t("settings.rules.desc")}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label={t("settings.rules.grace")}>
                <Input
                  type="number"
                  min={0}
                  max={120}
                  value={form.gracePeriodMins}
                  onChange={(e) => set("gracePeriodMins", Number(e.target.value))}
                />
              </Field>
              <Field label={t("settings.rules.late")}>
                <Input
                  type="number"
                  min={0}
                  max={240}
                  value={form.lateThresholdMins}
                  onChange={(e) => set("lateThresholdMins", Number(e.target.value))}
                />
              </Field>
              <Field label={t("settings.rules.minHours")}>
                <Input
                  type="number"
                  min={0}
                  max={24}
                  step={0.5}
                  value={form.minWorkHoursPerDay}
                  onChange={(e) => set("minWorkHoursPerDay", Number(e.target.value))}
                />
              </Field>
            </div>
            <Toggle
              label={t("settings.rules.requireApproval")}
              desc={t("settings.rules.requireApprovalDesc")}
              checked={form.requireApprovalForCorrections}
              onChange={(v) => set("requireApprovalForCorrections", v)}
            />
            <Toggle
              label={t("settings.rules.autoFlag")}
              desc={t("settings.rules.autoFlagDesc")}
              checked={form.autoFlagExceptions}
              onChange={(v) => set("autoFlagExceptions", v)}
            />
          </SettingsCard>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4">
          {requestSettings && (
            <RequestsSettingsTab
              initial={requestSettings}
              onSaved={(s) => setRequestSettings(requestSlice(s))}
            />
          )}
        </TabsContent>

        <TabsContent value="hours" className="space-y-4">
          <SettingsCard title={t("settings.hours.title")} desc={t("settings.hours.desc")}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("settings.hours.start")}>
                <Input
                  type="time"
                  value={form.defaultWorkStartTime}
                  onChange={(e) => set("defaultWorkStartTime", e.target.value)}
                />
              </Field>
              <Field label={t("settings.hours.end")}>
                <Input
                  type="time"
                  value={form.defaultWorkEndTime}
                  onChange={(e) => set("defaultWorkEndTime", e.target.value)}
                />
              </Field>
            </div>
            {/* Working days (BR-190). The sweep runs seven days a week without
                this, so every employee is told they missed attendance at the
                weekend. ISO weekday numbers: 1 = Monday … 7 = Sunday. */}
            <div className="mt-4">
              <p className="text-xs font-medium">{t("settings.hours.workingDays")}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[
                  { n: 1, l: "Mon" }, { n: 2, l: "Tue" }, { n: 3, l: "Wed" },
                  { n: 4, l: "Thu" }, { n: 5, l: "Fri" }, { n: 6, l: "Sat" },
                  { n: 7, l: "Sun" },
                ].map((d) => {
                  const on = form.workingDays.includes(d.n);
                  return (
                    <button
                      key={d.n}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        set(
                          "workingDays",
                          (on
                            ? form.workingDays.filter((x) => x !== d.n)
                            : [...form.workingDays, d.n]
                          ).sort((a, b) => a - b),
                        )
                      }
                      className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                        on
                          ? "border-primary bg-primary/10 font-medium text-foreground"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {d.l}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {t("settings.hours.workingDaysDesc")}
              </p>
            </div>
          </SettingsCard>

          <SettingsCard
            title="Check-in mode"
            desc="Controls how many check-in/out pairs an employee can record per day on mobile."
          >
            <div className="space-y-2">
              <ModeOption
                label="Single session per day"
                desc="Employees check in once and check out once. Classic office policy."
                value="SINGLE_SESSION_PER_DAY"
                checked={form.attendanceMode === "SINGLE_SESSION_PER_DAY"}
                onSelect={() => set("attendanceMode", "SINGLE_SESSION_PER_DAY")}
              />
              <ModeOption
                label="Multiple sessions per day"
                desc="Employees can check in and out multiple times. Switching sites auto-closes the prior session. Use this for field workers or split shifts."
                value="MULTIPLE_SESSIONS_PER_DAY"
                checked={form.attendanceMode === "MULTIPLE_SESSIONS_PER_DAY"}
                onSelect={() => set("attendanceMode", "MULTIPLE_SESSIONS_PER_DAY")}
              />
            </div>
          </SettingsCard>
        </TabsContent>

        <TabsContent value="geo" className="space-y-4">
          <SettingsCard title={t("settings.geo.title")} desc={t("settings.geo.desc")}>
            <Toggle
              label={t("settings.geo.enforce")}
              desc={t("settings.geo.enforceDesc")}
              checked={form.geofenceEnforcementEnabled}
              onChange={(v) => set("geofenceEnforcementEnabled", v)}
            />
            <Toggle
              label={t("settings.geo.gpsFallback")}
              desc={t("settings.geo.gpsFallbackDesc")}
              checked={form.allowGpsOnlyFallback}
              onChange={(v) => set("allowGpsOnlyFallback", v)}
            />
            <Field
              label={t("settings.geo.defaultRadius")}
              hint={
                inheritingSites === null
                  ? undefined
                  : inheritingSites === 0
                    ? "No work location inherits this — every one sets its own radius."
                    : `${inheritingSites} work location${inheritingSites === 1 ? "" : "s"} inherit this. Changing it resizes ${inheritingSites === 1 ? "its" : "their"} geofence, which changes who can check in there.`
              }
            >
              <Input
                type="number"
                min={10}
                max={5000}
                value={form.defaultGeofenceRadiusM}
                onChange={(e) => set("defaultGeofenceRadiusM", Number(e.target.value))}
              />
            </Field>
            <Toggle
              label={t("settings.geo.wfh")}
              desc={t("settings.geo.wfhDesc")}
              checked={form.wfhEnabled}
              onChange={(v) => set("wfhEnabled", v)}
            />
            <Field label={t("settings.geo.homeRadius")}>
              <Input
                type="number"
                min={50}
                max={1000}
                value={form.defaultHomeRadiusM}
                onChange={(e) => set("defaultHomeRadiusM", Number(e.target.value))}
              />
            </Field>
            <Toggle
              label={t("settings.geo.wfa")}
              desc={t("settings.geo.wfaDesc")}
              checked={form.wfaEnabled}
              onChange={(v) => set("wfaEnabled", v)}
            />
            <Toggle
              label={t("settings.geo.webCheckIn")}
              desc={t("settings.geo.webCheckInDesc")}
              checked={form.webCheckInEnabled}
              onChange={(v) => set("webCheckInEnabled", v)}
            />
            <Field label={t("settings.geo.maxAccuracy")} hint={t("settings.geo.maxAccuracyDesc")}>
              <Input
                type="number"
                min={20}
                max={1000}
                value={form.maxCheckInAccuracyM}
                onChange={(e) => set("maxCheckInAccuracyM", Number(e.target.value))}
              />
            </Field>
          </SettingsCard>
        </TabsContent>

        <TabsContent value="notif" className="space-y-4">
          <SettingsCard title={t("settings.notif.title")} desc={t("settings.notif.desc")}>
            <Toggle
              label={t("settings.notif.email")}
              desc={t("settings.notif.emailDesc")}
              checked={form.emailDigestEnabled}
              onChange={(v) => set("emailDigestEnabled", v)}
            />
            <Toggle
              label={t("settings.notif.inApp")}
              desc={t("settings.notif.inAppDesc")}
              checked={form.inAppNotificationsEnabled}
              onChange={(v) => set("inAppNotificationsEnabled", v)}
            />
            <Toggle
              label={t("settings.notif.push")}
              desc={t("settings.notif.pushDesc")}
              checked={form.mobilePushEnabled}
              onChange={(v) => set("mobilePushEnabled", v)}
            />
          </SettingsCard>
        </TabsContent>

        <TabsContent value="data" className="space-y-4">
          <SettingsCard title={t("settings.data.title")} desc={t("settings.data.desc")}>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => (window.location.href = "/attendance")}
              >
                {t("settings.data.exportAttendance")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => (window.location.href = "/employees")}
              >
                {t("settings.data.exportEmployees")}
              </Button>
            </div>
          </SettingsCard>
        </TabsContent>

        <TabsContent value="ai" className="space-y-4">
          <SettingsCard title={t("settings.ai.title")} desc={t("settings.ai.desc")}>
            <Toggle
              label={t("settings.ai.enableHR")}
              desc={t("settings.ai.enableHRDesc")}
              checked={form.aiEnableHR}
              onChange={(v) => set("aiEnableHR", v)}
            />
            <Toggle
              label={t("settings.ai.enableSup")}
              desc={t("settings.ai.enableSupDesc")}
              checked={form.aiEnableSup}
              onChange={(v) => set("aiEnableSup", v)}
            />
            <Toggle
              label={t("settings.ai.enableMobile")}
              desc={t("settings.ai.enableMobileDesc")}
              checked={form.aiEnableMobile}
              onChange={(v) => set("aiEnableMobile", v)}
            />
            <Field label={t("settings.ai.responseStyle")}>
              <Select
                value={form.aiResponseStyle}
                onValueChange={(v) => set("aiResponseStyle", v as "concise" | "detailed")}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="concise">{t("settings.ai.concise")}</SelectItem>
                  <SelectItem value="detailed">{t("settings.ai.detailed")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </SettingsCard>
        </TabsContent>
      </Tabs>

      {tab !== "requests" && (
        <div className="sticky bottom-0 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
              {t("common.reset")}
            </Button>
            <Button
              className="bg-gradient-primary"
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {saving ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

function SettingsCard({
  title,
  desc,
  children,
}: {
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5 space-y-4">
      <div>
        <h3 className="font-display font-semibold">{title}</h3>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      {children}
    </Card>
  );
}


const Toggle = ({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <div className="flex items-center justify-between rounded-lg border p-3">
    <div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const ModeOption = ({
  label,
  desc,
  value,
  checked,
  onSelect,
}: {
  label: string;
  desc: string;
  value: string;
  checked: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
      checked
        ? "border-primary bg-primary/5"
        : "hover:border-muted-foreground/40"
    }`}
  >
    <div
      className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border ${
        checked ? "border-primary bg-primary" : "border-muted-foreground/40"
      }`}
      aria-checked={checked}
      role="radio"
      data-value={value}
    />
    <div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
  </button>
);

export default Settings;
